import { Link } from "@tanstack/react-router"
import {
	IconCopyOutline18 as Copy,
	IconLinkOutline18 as LinkIcon,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton, ImportantIcon, LinearIcon, Tooltip } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as EllipsisVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"

import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { getPortalBaseUrl } from "@/lib/portal-base-url"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"

interface LinkedItemActionsProps {
	itemId: string
	itemType: "issue" | "project"
	linearUrl: string | null
	label: string
	isRoadmapVisible: boolean
	isImportant: boolean
	onRemoveLink: (id: string) => Promise<void>
	onToggleImportant: (id: string, isImportant: boolean) => Promise<void>
	className?: string
}

export function LinkedItemActions({
	itemId,
	itemType,
	linearUrl,
	label,
	isRoadmapVisible,
	isImportant,
	onRemoveLink,
	onToggleImportant,
	className,
}: LinkedItemActionsProps) {
	const { role } = useRole()
	const workspace = useZeroWorkspace()

	const portalBaseUrl = getPortalBaseUrl(workspace)

	const isSupportPortalEnabled =
		workspace?.customerPortalSettings?.isEnabled === true
	const isRoadmapPublic = workspace?.domainPublic === true

	return (
		<div
			className={cn(
				"absolute right-[3px] top-[6px] flex items-center gap-1 px-1 group-hover:bg-white dark:group-hover:bg-gray-800",
				className,
			)}
		>
			{linearUrl && (
				<Tooltip label={`Open ${itemType} in Linear`}>
					<Link
						to={linearUrl}
						target="_blank"
						className="opacity-0 group-hover:opacity-100"
						onClick={(e) => {
							e.stopPropagation()
						}}
					>
						<IconButton
							Icon={LinearIcon}
							size="md"
							variant="ghost"
							className="hover:bg-gray-200 dark:hover:bg-gray-600"
						/>
					</Link>
				</Tooltip>
			)}

			<div
				className={cn({
					"opacity-0 group-hover:opacity-100": !isImportant,
				})}
			>
				<Tooltip
					label={isImportant ? "Important" : "Mark this request as important"}
				>
					<IconButton
						Icon={ImportantIcon}
						size="md"
						variant="ghost"
						disabled={role === "VIEWER"}
						className={cn("hover:bg-gray-200 dark:hover:bg-gray-600", {
							"text-gray-300 hover:text-black hover:dark:text-white":
								!isImportant,
						})}
						// Ghost IconButton forces text-tertiary on its icon, so the red has to sit on the icon.
						iconClassName={cn("h-[0.8rem] w-[0.8rem]", {
							"!text-red-400": isImportant,
						})}
						onClick={async (e: React.MouseEvent) => {
							e.preventDefault()
							e.stopPropagation()

							if (role === "VIEWER") {
								return
							}

							await onToggleImportant(itemId, !isImportant)
						}}
					/>
				</Tooltip>
			</div>

			<RoleCheck not={["VIEWER"]}>
				<DropdownMenu
					contentProps={{ align: "end" }}
					trigger={
						<IconButton
							Icon={EllipsisVertical}
							variant="ghost"
							size="md"
							iconClassName="fill-current"
							className="relative hover:bg-gray-200 dark:hover:bg-gray-600"
							type="button"
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
							}}
						/>
					}
				>
					{isSupportPortalEnabled && (
						<DropdownMenuItem
							Icon={Copy}
							onClick={async (e) => {
								e.preventDefault()
								e.stopPropagation()

								const url = `${portalBaseUrl}requests?r=${itemId}`
								try {
									await navigator.clipboard.writeText(url)
									toast.success({
										title: "Support portal link copied",
										time: 5500,
										descriptionAction: linearUrl
											? {
													label,
													callback: () => window.open(linearUrl, "_blank"),
												}
											: undefined,
										description: (
											<span className="mt-2 block">
												Only this customer and their team members can open this
												link.
											</span>
										),
									})
								} catch {
									toast.error({ title: "Failed to copy link to clipboard" })
								}
							}}
						>
							Copy Support Portal link
						</DropdownMenuItem>
					)}
					{isRoadmapPublic && isRoadmapVisible && (
						<DropdownMenuItem
							Icon={LinkIcon}
							onClick={async (e) => {
								e.preventDefault()
								e.stopPropagation()

								const url = `${portalBaseUrl}roadmap?id=${itemId}`
								try {
									await navigator.clipboard.writeText(url)
									toast.success({
										title: "Roadmap link copied",
										descriptionAction: linearUrl
											? {
													label,
													callback: () => window.open(linearUrl, "_blank"),
												}
											: undefined,
									})
								} catch {
									toast.error({ title: "Failed to copy link to clipboard" })
								}
							}}
						>
							Copy Roadmap link
						</DropdownMenuItem>
					)}
					<DropdownMenuItem
						Icon={X}
						onClick={async (e) => {
							e.preventDefault()
							e.stopPropagation()
							await onRemoveLink(itemId)
						}}
					>
						Remove link
					</DropdownMenuItem>
				</DropdownMenu>
			</RoleCheck>
		</div>
	)
}
